import { clsx } from 'clsx';
import { ABILITY_NAMES } from '@character-creator/types';
import type { CharacterBenefits } from '../../hooks/useCharacterBenefits';
import { formatAbility, formatSkill } from '../../utils/formatting';

interface BenefitsSummaryProps {
  benefits: CharacterBenefits;
}

export function BenefitsSummary({ benefits }: BenefitsSummaryProps) {
  // Format hit dice display for multiclass
  const hitDiceDisplay = benefits.hitDice.length > 0
    ? benefits.hitDice.map((hd) => `${hd.count}${hd.die}`).join(' + ')
    : benefits.hitDie;

  return (
    <div className="space-y-6">
      {/* Combat Stats */}
      <Section title="Combat">
        <div className="grid grid-cols-2 gap-2">
          <StatBox label="Level" value={benefits.characterLevel.toString()} />
          <StatBox label="HP" value={benefits.hitPoints.toString()} />
          <StatBox label="Speed" value={`${benefits.speed} ft`} />
          <StatBox label="Prof." value={`+${benefits.proficiencyBonus}`} />
        </div>
        {/* Hit Dice */}
        <div className="mt-2 text-xs text-center">
          <span className="text-text-muted">Hit Dice: </span>
          <span className="text-text-primary font-medium">{hitDiceDisplay}</span>
        </div>
      </Section>

      {/* Ability Scores */}
      <Section title="Abilities">
        <div className="grid grid-cols-3 gap-2">
          {ABILITY_NAMES.map((ability) => {
            const racialBonus = benefits.racialBonuses[ability] || 0;
            const asiBonus = benefits.asiAbilityIncreases[ability] || 0;
            const totalBonus = racialBonus + asiBonus;
            const final = benefits.finalScores[ability];
            const mod = benefits.modifiers[ability];
            const modStr = mod >= 0 ? `+${mod}` : `${mod}`;

            return (
              <div key={ability} className="stat-block text-center p-2">
                <div className="text-xs text-text-muted uppercase">
                  {ability.slice(0, 3)}
                </div>
                <div className="text-lg font-display font-bold text-text-primary">
                  {final}
                  {totalBonus !== 0 && (
                    <span className="text-xs text-accent-gold ml-1">
                      +{totalBonus}
                    </span>
                  )}
                </div>
                <div
                  className={clsx(
                    'text-xs font-medium',
                    mod >= 0 ? 'text-success' : 'text-error'
                  )}
                >
                  {modStr}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Proficiencies */}
      <Section title="Proficiencies">
        <div className="space-y-3 text-xs">
          {benefits.savingThrows.length > 0 && (
            <ProficiencyRow
              label="Saves"
              items={benefits.savingThrows.map(formatAbility)}
            />
          )}
          {benefits.skills.length > 0 && (
            <ProficiencyRow
              label="Skills"
              items={benefits.skills.map(formatSkill)}
            />
          )}
          {benefits.armor.length > 0 && (
            <ProficiencyRow
              label="Armor"
              items={benefits.armor.map(formatAbility)}
            />
          )}
          {benefits.weapons.length > 0 && (
            <ProficiencyRow label="Weapons" items={benefits.weapons} />
          )}
          {benefits.tools.length > 0 && (
            <ProficiencyRow label="Tools" items={benefits.tools} />
          )}
          {benefits.languages.length > 0 && (
            <ProficiencyRow label="Languages" items={benefits.languages} />
          )}
          {benefits.isCommoner &&
            benefits.savingThrows.length === 0 &&
            benefits.armor.length === 0 && (
              <p className="text-text-muted italic">
                Commoner: No class proficiencies
              </p>
            )}
        </div>
      </Section>

      {/* Racial Traits */}
      {benefits.racialTraits.length > 0 && (
        <Section title="Racial Traits">
          <ul className="space-y-2 text-xs">
            {benefits.racialTraits.map((trait) => (
              <li key={trait.id}>
                <span className="font-medium text-text-primary">{trait.name}</span>
                <p className="text-text-muted mt-0.5 line-clamp-2">
                  {trait.description}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Class Features */}
      {benefits.classFeatures.length > 0 && (
        <Section title="Class Features">
          <ul className="space-y-2 text-xs">
            {benefits.classFeatures.map((feature) => (
              <li key={feature.id}>
                <span className="font-medium text-text-primary">{feature.name}</span>
                <p className="text-text-muted mt-0.5 line-clamp-2">
                  {feature.description}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Spellcasting */}
      {benefits.spellcasting && (
        <Section title="Spellcasting">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-text-muted">Ability:</span>
              <span className="ml-1 text-text-primary">
                {formatAbility(benefits.spellcasting.ability)}
              </span>
            </div>
            <div>
              <span className="text-text-muted">Attack:</span>
              <span className="ml-1 text-text-primary">
                +{benefits.spellcasting.attackBonus}
              </span>
            </div>
            <div>
              <span className="text-text-muted">Save DC:</span>
              <span className="ml-1 text-text-primary">
                {benefits.spellcasting.saveDC}
              </span>
            </div>
            <div>
              <span className="text-text-muted">Cantrips:</span>
              <span className="ml-1 text-text-primary">
                {benefits.spellcasting.cantripsKnown}
              </span>
            </div>
          </div>
        </Section>
      )}

      {/* Feats */}
      {benefits.feats.length > 0 && (
        <Section title="Feats">
          <ul className="space-y-2 text-xs">
            {benefits.feats.map((feat) => (
              <li key={feat.id}>
                <span className="font-medium text-text-primary">{feat.name}</span>
                <p className="text-text-muted mt-0.5">{feat.description}</p>
                <ul className="mt-1 text-text-secondary list-disc list-inside">
                  {feat.benefits.map((benefit, i) => (
                    <li key={i}>{benefit}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Familiar */}
      {benefits.familiar && (
        <Section title="Familiar">
          <div className="text-xs">
            <div className="font-medium text-text-primary mb-1">{benefits.familiar.name}</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <span className="text-text-muted">AC:</span>
                <span className="ml-1 text-text-primary">{benefits.familiar.armorClass}</span>
              </div>
              <div>
                <span className="text-text-muted">HP:</span>
                <span className="ml-1 text-text-primary">{benefits.familiar.hitPoints}</span>
              </div>
              <div>
                <span className="text-text-muted">Speed:</span>
                <span className="ml-1 text-text-primary">
                  {formatSpeed(benefits.familiar.speed)}
                </span>
              </div>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
        {title}
      </h4>
      {children}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat-block text-center p-2">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="text-lg font-display font-bold text-accent-gold">{value}</div>
    </div>
  );
}

function ProficiencyRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <span className="text-text-muted">{label}:</span>
      <span className="ml-1 text-text-primary">{items.join(', ')}</span>
    </div>
  );
}

function formatSpeed(speed: { walk?: number; fly?: number; swim?: number; climb?: number; burrow?: number }): string {
  const parts: string[] = [];
  if (speed.walk) parts.push(`${speed.walk} ft`);
  if (speed.fly) parts.push(`fly ${speed.fly} ft`);
  if (speed.swim) parts.push(`swim ${speed.swim} ft`);
  if (speed.climb) parts.push(`climb ${speed.climb} ft`);
  if (speed.burrow) parts.push(`burrow ${speed.burrow} ft`);
  return parts.join(', ') || '0 ft';
}
